import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { wabaApi } from "../../lib/wabaApi";

// Aba de TESTE dos endpoints de anúncios (Marketing API + Conversions API de
// mensagens): escolhe a conexão, lista contas, puxa insights e dispara um
// evento, mostrando o JSON cru. Nada de gráfico ou cruzamento — isso vem por
// cima dos endpoints.

interface Conn { id: string; name: string; ads_connected: boolean; waba_id: string | null }
interface Account { id: string; account_id: string; name: string; currency: string; account_status: number }
interface Campaign {
  id: string; name: string; status: string; effective_status: string; objective: string;
  daily_budget?: string; lifetime_budget?: string;
  insights?: { data: { spend?: string; impressions?: string; reach?: string; clicks?: string; actions?: { action_type: string; value: string }[] }[] };
}

interface Creative {
  id: string; name?: string; title?: string; body?: string; call_to_action_type?: string;
  thumbnail_url?: string; image_url?: string;
  object_story_spec?: { link_data?: any; video_data?: any; photo_data?: any };
  asset_feed_spec?: { bodies?: { text: string }[]; titles?: { text: string }[]; call_to_action_types?: string[] };
}
interface Ad {
  id: string; name: string; status: string; effective_status: string; preview_shareable_link?: string;
  adset?: { id: string; name: string; status: string; effective_status: string; daily_budget?: string; lifetime_budget?: string; optimization_goal?: string };
  creative?: Creative;
  insights?: Campaign["insights"];
}

// A Meta espalha a copy por três lugares conforme o tipo de criativo: campos
// diretos, object_story_spec (post/vídeo) ou asset_feed_spec (dinâmico).
function creativeOf(cr?: Creative) {
  const ld = cr?.object_story_spec?.link_data ?? cr?.object_story_spec?.video_data ?? cr?.object_story_spec?.photo_data ?? {};
  const afs = cr?.asset_feed_spec;
  return {
    body: cr?.body || ld.message || afs?.bodies?.[0]?.text || "",
    title: cr?.title || ld.name || ld.title || afs?.titles?.[0]?.text || "",
    cta: cr?.call_to_action_type || ld.call_to_action?.type || afs?.call_to_action_types?.[0] || "",
    img: cr?.image_url || cr?.thumbnail_url || ld.picture || "",
    extraBodies: Math.max(0, (afs?.bodies?.length ?? 0) - 1),
  };
}

const CONV = "onsite_conversion.messaging_conversation_started_7d";
const num = (v?: string) => Number(v ?? 0).toLocaleString("pt-BR");
const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
// Orçamento vem em centavos; gasto dos insights vem em reais (string). Meta é assim.
function campRow(c: Campaign) {
  const i = c.insights?.data?.[0] ?? {};
  const spend = Number(i.spend ?? 0);
  const conv = Number(i.actions?.find((a) => a.action_type === CONV)?.value ?? 0);
  const budget = c.daily_budget ? `${brl(Number(c.daily_budget) / 100)}/dia` : c.lifetime_budget ? brl(Number(c.lifetime_budget) / 100) : "—";
  return { spend, conv, budget, imp: i.impressions, clicks: i.clicks, cpc: conv ? brl(spend / conv) : "—" };
}

const EVENTS = ["Purchase", "LeadSubmitted", "QualifiedLead", "InitiateCheckout", "AddToCart",
  "ViewContent", "OrderCreated", "OrderShipped", "OrderDelivered", "OrderCanceled",
  "OrderReturned", "CartAbandoned", "RatingProvided", "ReviewProvided"];
const PRESETS = ["today", "yesterday", "last_7d", "last_30d", "this_month", "last_month", "maximum"];

const input: React.CSSProperties = { padding: "7px 10px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" };
const btn: React.CSSProperties = { padding: "7px 14px", background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const label: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)", display: "flex", flexDirection: "column", gap: 4 };

function errText(err: any): string {
  const d = err?.payload?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return JSON.stringify(d, null, 2);
  return String(err?.message || err);
}

function Json({ value }: { value: unknown }) {
  if (value === undefined) return null;
  return (
    <pre style={{ margin: 0, padding: 12, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 11, color: "var(--ink)", overflow: "auto", maxHeight: 420 }}>
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function AdSets({ campaignId, ads, busy, onToggle }: {
  campaignId: string; ads: Ad[]; busy: boolean;
  onToggle: (campaignId: string, id: string, current: string, nome: string) => void;
}) {
  // Agrupa por conjunto preservando a ordem em que a Meta devolveu.
  const groups: { adset: NonNullable<Ad["adset"]>; ads: Ad[] }[] = [];
  for (const ad of ads) {
    const as = ad.adset ?? { id: "?", name: "(sem conjunto)", status: "", effective_status: "" };
    let g = groups.find((x) => x.adset.id === as.id);
    if (!g) { g = { adset: as, ads: [] }; groups.push(g); }
    g.ads.push(ad);
  }
  const small: React.CSSProperties = { padding: "3px 9px", borderRadius: 6, border: "1px solid var(--line-strong)", background: "transparent", fontSize: 11, cursor: "pointer" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {groups.map(({ adset, ads: list }) => {
        const asActive = adset.effective_status === "ACTIVE";
        const budget = adset.daily_budget ? `${brl(Number(adset.daily_budget) / 100)}/dia` : adset.lifetime_budget ? brl(Number(adset.lifetime_budget) / 100) : "";
        return (
          <div key={adset.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "var(--ink)" }}>Conjunto · {adset.name}</span>
              <span style={{ color: asActive ? "var(--ok)" : "var(--ink-muted)" }}>{adset.effective_status}</span>
              {budget && <span style={{ color: "var(--ink-muted)" }}>{budget}</span>}
              {adset.optimization_goal && <span style={{ color: "var(--ink-muted)" }}>{adset.optimization_goal}</span>}
              {adset.id !== "?" && (
                <button disabled={busy} onClick={() => onToggle(campaignId, adset.id, adset.effective_status, adset.name)}
                  style={{ ...small, color: asActive ? "var(--danger)" : "var(--ok)" }}>{asActive ? "Pausar" : "Reativar"}</button>
              )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {list.map((ad) => {
                const cr = creativeOf(ad.creative);
                const r = campRow(ad as unknown as Campaign);
                const adActive = ad.effective_status === "ACTIVE";
                return (
                  <div key={ad.id} style={{ display: "flex", gap: 10, width: 440, maxWidth: "100%", padding: 10, border: "1px solid var(--line)", borderRadius: 8, background: "var(--surface-input)", fontSize: 12, color: "var(--ink)" }}>
                    {cr.img
                      ? <img src={cr.img} alt="" style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 6, flexShrink: 0, background: "var(--surface-hover)" }} />
                      : <div style={{ width: 84, height: 84, borderRadius: 6, flexShrink: 0, background: "var(--surface-hover)" }} />}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ad.name}</span>
                        <span style={{ fontSize: 11, color: adActive ? "var(--ok)" : "var(--ink-muted)", flexShrink: 0 }}>{ad.effective_status}</span>
                      </div>
                      {cr.title && <div style={{ fontWeight: 500 }}>{cr.title}</div>}
                      {cr.body && <div style={{ color: "var(--ink-secondary)", whiteSpace: "pre-wrap", maxHeight: 96, overflow: "auto" }}>{cr.body}{cr.extraBodies > 0 && <span style={{ color: "var(--ink-muted)" }}> (+{cr.extraBodies} variações)</span>}</div>}
                      {cr.cta && <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>CTA: {cr.cta}</div>}
                      <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                        {brl(r.spend)} · {num(r.imp)} impr · {num(r.clicks)} cliques · {r.conv} conversas · {r.cpc}/conversa
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                        {ad.preview_shareable_link && <a href={ad.preview_shareable_link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--brand)" }}>Preview na Meta</a>}
                        <button disabled={busy} onClick={() => onToggle(campaignId, ad.id, ad.effective_status, ad.name)}
                          style={{ ...small, color: adActive ? "var(--danger)" : "var(--ok)" }}>{adActive ? "Pausar" : "Reativar"}</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function WabaMarketingTab() {
  const { data: conns = [] } = useQuery<Conn[]>({ queryKey: ["waba-connections"], queryFn: () => wabaApi.get("/connections") });
  const [connId, setConnId] = useState("");
  const conn = conns.find((c) => c.id === connId);

  // Insights
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [account, setAccount] = useState("");
  const [level, setLevel] = useState("ad");
  const [preset, setPreset] = useState("last_30d");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [inc, setInc] = useState("");
  const [fields, setFields] = useState("");
  const [insights, setInsights] = useState<unknown>();
  const [busy, setBusy] = useState(false);

  // Espelho de tráfego pago
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [onlyActive, setOnlyActive] = useState(true);
  const [campErr, setCampErr] = useState<string | null>(null);
  // Drill-down por campanha: "loading" | erro (string) | anúncios
  const [expanded, setExpanded] = useState<Record<string, Ad[] | string>>({});

  async function loadAds(campaignId: string) {
    setExpanded((m) => ({ ...m, [campaignId]: "loading" }));
    try {
      const r = await wabaApi.get<{ data: Ad[] }>(`/connections/${connId}/ads/campaigns/${campaignId}/ads?date_preset=${preset}`);
      setExpanded((m) => ({ ...m, [campaignId]: r.data ?? [] }));
    } catch (e) { setExpanded((m) => ({ ...m, [campaignId]: errText(e) })); }
  }

  function toggleExpand(campaignId: string) {
    if (expanded[campaignId] !== undefined) {
      setExpanded((m) => { const n = { ...m }; delete n[campaignId]; return n; });
    } else loadAds(campaignId);
  }

  async function toggleObject(campaignId: string, id: string, current: string, nome: string) {
    const status = current === "ACTIVE" ? "PAUSED" : "ACTIVE";
    if (!confirm(`${status === "PAUSED" ? "Pausar" : "Reativar"} "${nome}"?`)) return;
    try {
      await wabaApi.post(`/connections/${connId}/ads/objects/${id}/status`, { status });
      await loadAds(campaignId);
    } catch (e) { setCampErr(errText(e)); }
  }

  // Status (pausar/reativar)
  const [objId, setObjId] = useState("");
  const [objStatus, setObjStatus] = useState("PAUSED");
  const [statusOut, setStatusOut] = useState<unknown>();

  // Evento
  const [evName, setEvName] = useState("QualifiedLead");
  const [clid, setClid] = useState("");
  const [phone, setPhone] = useState("");
  const [value, setValue] = useState("");
  const [evId, setEvId] = useState("");
  const [evOut, setEvOut] = useState<unknown>();

  async function run(fn: () => Promise<unknown>, set: (v: unknown) => void) {
    setBusy(true);
    try { set(await fn()); } catch (e) { set(errText(e)); } finally { setBusy(false); }
  }

  function loadAccounts() {
    run(async () => {
      const a = await wabaApi.get<Account[]>(`/connections/${connId}/ads/accounts`);
      setAccounts(a); if (a[0] && !account) setAccount(a[0].id);
      return a;
    }, (v) => { if (typeof v === "string") setInsights(v); });
  }

  // Conexão com anúncios → contas na hora, sem clique.
  useEffect(() => { if (conn?.ads_connected) loadAccounts(); }, [connId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conta escolhida → campanhas na hora; filtros também recarregam.
  useEffect(() => { if (account) loadCampaigns(); else setCampaigns(null); }, [account, preset, onlyActive]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadCampaigns() {
    setBusy(true); setCampErr(null);
    try {
      const r = await wabaApi.get<{ data: Campaign[] }>(`/connections/${connId}/ads/campaigns?account=${account}&date_preset=${preset}&active=${onlyActive}`);
      setCampaigns(r.data ?? []);
    } catch (e) { setCampErr(errText(e)); } finally { setBusy(false); }
  }

  async function toggleCampaign(c: Campaign) {
    const status = c.effective_status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    if (!confirm(`${status === "PAUSED" ? "Pausar" : "Reativar"} a campanha "${c.name}"?`)) return;
    setBusy(true);
    try {
      await wabaApi.post(`/connections/${connId}/ads/objects/${c.id}/status`, { status });
      await loadCampaigns();
    } catch (e) { setCampErr(errText(e)); setBusy(false); }
  }

  function loadInsights() {
    const q = new URLSearchParams({ account, level });
    if (since && until) { q.set("since", since); q.set("until", until); } else q.set("date_preset", preset);
    if (inc) q.set("time_increment", inc);
    if (fields.trim()) q.set("fields", fields.trim());
    run(() => wabaApi.get(`/connections/${connId}/ads/insights?${q}`), setInsights);
  }

  function setStatus() {
    run(() => wabaApi.post(`/connections/${connId}/ads/objects/${objId.trim()}/status`, { status: objStatus }), setStatusOut);
  }

  function sendEvent() {
    const body: Record<string, unknown> = { event_name: evName };
    if (clid) body.ctwa_clid = clid; else if (phone) body.phone = phone;
    if (value) body.value = Number(value);
    if (evId) body.event_id = evId;
    run(() => wabaApi.post(`/connections/${connId}/ads/events`, body), setEvOut);
  }

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 24, maxWidth: 1000 }}>
      <label style={label}>Conexão
        <select style={input} value={connId} onChange={(e) => { setConnId(e.target.value); setAccounts(null); setAccount(""); setInsights(undefined); setEvOut(undefined); }}>
          <option value="">— escolha —</option>
          {conns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.ads_connected ? " · anúncios ok" : ""}</option>)}
        </select>
      </label>

      {conn && (
        <>
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Tráfego pago · campanhas</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <label style={label}>Conta
                <select style={{ ...input, minWidth: 220 }} value={account} onChange={(e) => setAccount(e.target.value)}>
                  <option value="">—</option>
                  {(accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
                </select>
              </label>
              <label style={label}>Período
                <select style={input} value={preset} onChange={(e) => setPreset(e.target.value)}>
                  {PRESETS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </label>
              <label style={{ ...label, flexDirection: "row", alignItems: "center", gap: 6, paddingBottom: 8 }}>
                <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} /> só ativas
              </label>
              <button style={btn} disabled={busy || !account} onClick={loadCampaigns}>Atualizar</button>
            </div>
            {campErr && <Json value={campErr} />}
            {!campaigns && !campErr && account && <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando campanhas…</div>}
            {campaigns && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12, color: "var(--ink)" }}>
                  <thead>
                    <tr style={{ color: "var(--ink-muted)", textAlign: "left" }}>
                      {["Campanha", "Status", "Objetivo", "Orçamento", "Gasto", "Impressões", "Cliques", "Conversas", "Custo/conversa", ""].map((h) => (
                        <th key={h} style={{ padding: "6px 8px", borderBottom: "1px solid var(--line)", fontWeight: 500, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length === 0 && <tr><td colSpan={10} style={{ padding: 12, color: "var(--ink-muted)" }}>Nenhuma campanha.</td></tr>}
                    {campaigns.map((c) => {
                      const r = campRow(c);
                      const active = c.effective_status === "ACTIVE";
                      const ads = expanded[c.id];
                      return [
                        <tr key={c.id} style={{ borderBottom: ads === undefined ? "1px solid var(--line)" : "none" }}>
                          <td style={{ padding: "6px 8px" }}>
                            <button onClick={() => toggleExpand(c.id)} title="Conjuntos e anúncios"
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-muted)", padding: "0 6px 0 0", fontSize: 11 }}>
                              {ads === undefined ? "▶" : "▼"}
                            </button>
                            {c.name}<div style={{ fontSize: 10, color: "var(--ink-muted)", paddingLeft: 18 }}>{c.id}</div>
                          </td>
                          <td style={{ padding: "6px 8px", color: active ? "var(--ok)" : "var(--ink-muted)" }}>{c.effective_status}</td>
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{c.objective}</td>
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{r.budget}</td>
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{brl(r.spend)}</td>
                          <td style={{ padding: "6px 8px" }}>{num(r.imp)}</td>
                          <td style={{ padding: "6px 8px" }}>{num(r.clicks)}</td>
                          <td style={{ padding: "6px 8px" }}>{r.conv.toLocaleString("pt-BR")}</td>
                          <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{r.cpc}</td>
                          <td style={{ padding: "6px 8px" }}>
                            <button disabled={busy} onClick={() => toggleCampaign(c)}
                              style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--line-strong)", background: "transparent", color: active ? "var(--danger)" : "var(--ok)", fontSize: 11, cursor: "pointer" }}>
                              {active ? "Pausar" : "Reativar"}
                            </button>
                          </td>
                        </tr>,
                        ads !== undefined && (
                          <tr key={`${c.id}-ads`} style={{ borderBottom: "1px solid var(--line)" }}>
                            <td colSpan={10} style={{ padding: "4px 8px 14px 26px", background: "var(--surface-card)" }}>
                              {ads === "loading" ? <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando anúncios…</div>
                                : typeof ads === "string" ? <Json value={ads} />
                                : ads.length === 0 ? <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhum anúncio.</div>
                                : <AdSets campaignId={c.id} ads={ads} busy={busy} onToggle={toggleObject} />}
                            </td>
                          </tr>
                        ),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--line)", paddingTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Leitura · Marketing API (JSON cru)</div>
            {!conn.ads_connected && <div style={{ fontSize: 12, color: "var(--warn)" }}>Esta conexão não tem anúncios vinculados — use "Conectar anúncios" no card da conexão.</div>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <button style={btn} disabled={busy || !conn.ads_connected} onClick={loadAccounts}>Listar contas</button>
              <label style={label}>Conta
                <select style={{ ...input, minWidth: 220 }} value={account} onChange={(e) => setAccount(e.target.value)}>
                  <option value="">—</option>
                  {(accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
                </select>
              </label>
              <label style={label}>Nível
                <select style={input} value={level} onChange={(e) => setLevel(e.target.value)}>
                  {["ad", "adset", "campaign", "account"].map((l) => <option key={l}>{l}</option>)}
                </select>
              </label>
              <label style={label}>Período
                <select style={input} value={preset} onChange={(e) => setPreset(e.target.value)}>
                  {PRESETS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </label>
              <label style={label}>since<input style={input} type="date" value={since} onChange={(e) => setSince(e.target.value)} /></label>
              <label style={label}>until<input style={input} type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></label>
              <label style={label}>time_increment<input style={{ ...input, width: 90 }} placeholder="1" value={inc} onChange={(e) => setInc(e.target.value)} /></label>
            </div>
            <label style={label}>fields (vazio = padrão)
              <input style={input} placeholder="campaign_name,ad_name,spend,actions" value={fields} onChange={(e) => setFields(e.target.value)} />
            </label>
            <button style={{ ...btn, alignSelf: "flex-start" }} disabled={busy || !account} onClick={loadInsights}>Buscar insights</button>
            <Json value={insights} />
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--line)", paddingTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Escrita · Marketing API (pausar / reativar)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <label style={label}>campaign_id / adset_id / ad_id<input style={{ ...input, minWidth: 220 }} value={objId} onChange={(e) => setObjId(e.target.value)} /></label>
              <label style={label}>status
                <select style={input} value={objStatus} onChange={(e) => setObjStatus(e.target.value)}>
                  <option>PAUSED</option><option>ACTIVE</option>
                </select>
              </label>
              <button style={btn} disabled={busy || !conn.ads_connected || !objId.trim()} onClick={setStatus}>Aplicar</button>
            </div>
            <Json value={statusOut} />
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--line)", paddingTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Escrita · Conversions API de mensagens</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <label style={label}>Evento
                <select style={input} value={evName} onChange={(e) => setEvName(e.target.value)}>
                  {EVENTS.map((n) => <option key={n}>{n}</option>)}
                </select>
              </label>
              <label style={label}>ctwa_clid<input style={{ ...input, minWidth: 260 }} value={clid} onChange={(e) => setClid(e.target.value)} /></label>
              <label style={label}>ou telefone do lead<input style={input} value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
              <label style={label}>value (BRL)<input style={{ ...input, width: 110 }} value={value} onChange={(e) => setValue(e.target.value)} /></label>
              <label style={label}>event_id<input style={input} value={evId} onChange={(e) => setEvId(e.target.value)} /></label>
              <button style={btn} disabled={busy || (!clid && !phone)} onClick={sendEvent}>Enviar evento</button>
            </div>
            <Json value={evOut} />
          </section>
        </>
      )}
    </div>
  );
}
